import React from 'react';
import { motion } from 'framer-motion';
import { Card, Skeleton } from 'antd';
import { useTheme } from '../../../contexts/ThemeContext';

// Skeleton for Goals Table
export const GoalsTableSkeleton = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`rounded-3xl overflow-hidden shadow-sm ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'
      }`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {/* Header */}
      <div className={`p-6 pb-2 flex items-center justify-between border-b ${
        isDark ? 'border-white/10' : 'border-slate-50'
      }`}>
        <Skeleton.Input 
          active 
          size="small" 
          style={{ width: 200, height: 16 }}
          className={isDark ? '!bg-white/10' : ''}
        />
        <Skeleton.Button 
          active 
          size="small" 
          style={{ width: 100, height: 32 }}
          className={isDark ? '!bg-white/10' : ''}
        />
      </div>

      {/* Table Skeleton */}
      <div className="p-6">
        <div className="space-y-3">
          {[1, 2, 3].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 40, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 200, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 100, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 80, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 60, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Skeleton for Soft Skills Section
export const SoftSkillsSkeleton = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`rounded-[2.5rem] overflow-hidden shadow-sm ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'
      }`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {/* Header */}
      <div className={`p-4 border-b ${
        isDark ? 'border-white/10' : 'border-slate-100'
      }`}>
        <Skeleton.Input 
          active 
          size="small" 
          style={{ width: 250, height: 18 }}
          className={isDark ? '!bg-white/10' : ''}
        />
      </div>

      {/* Skills Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
            >
              <Card
                className={`rounded-xl shadow-sm ${
                  isDark ? '!bg-white/5 !border-white/10' : '!bg-white !border-slate-100'
                }`}
                bodyStyle={{ padding: '16px' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton.Avatar 
                    active 
                    size={28} 
                    shape="square"
                    className={isDark ? '!bg-white/10' : ''}
                  />
                  <Skeleton.Input 
                    active 
                    size="small" 
                    style={{ width: 120, height: 14 }}
                    className={isDark ? '!bg-white/10' : ''}
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <Skeleton.Input 
                        active 
                        size="small" 
                        style={{ width: 100, height: 10 }}
                        className={isDark ? '!bg-white/10' : ''}
                      />
                      <Skeleton.Input 
                        active 
                        size="small" 
                        style={{ width: 40, height: 10 }}
                        className={isDark ? '!bg-white/10' : ''}
                      />
                    </div>
                    <Skeleton.Button 
                      active 
                      size="small" 
                      block
                      style={{ height: 8 }}
                      className={isDark ? '!bg-white/10' : ''}
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <Skeleton.Input 
                        active 
                        size="small" 
                        style={{ width: 100, height: 10 }}
                        className={isDark ? '!bg-white/10' : ''}
                      />
                      <Skeleton.Input 
                        active 
                        size="small" 
                        style={{ width: 40, height: 10 }}
                        className={isDark ? '!bg-white/10' : ''}
                      />
                    </div>
                    <Skeleton.Button 
                      active 
                      size="small" 
                      block
                      style={{ height: 8 }}
                      className={isDark ? '!bg-white/10' : ''}
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Skeleton for Review Summary
export const ReviewSummarySkeleton = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className={`rounded-3xl overflow-hidden shadow-sm ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'
      }`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {/* Header */}
      <div className={`p-4 border-b ${
        isDark ? 'border-white/10 bg-blue-500/10' : 'border-slate-100 bg-[#eef8ff]'
      }`}>
        <Skeleton.Input 
          active 
          size="small" 
          style={{ width: 200, height: 16 }}
          className={isDark ? '!bg-white/10' : ''}
        />
      </div>

      {/* Table Skeleton */}
      <div className="px-6 py-4">
        <div className="space-y-3">
          {[1, 2, 3].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 40, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 300, height: 20 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Button 
                active 
                size="small" 
                style={{ width: 100, height: 32 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Button 
                active 
                size="small" 
                style={{ width: 100, height: 32 }}
                className={isDark ? '!bg-white/10' : ''}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Skeleton for Goals Dashboard
export const GoalsDashboardSkeleton = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
          >
            <Card
              className={`rounded-2xl ${
                isDark ? '!bg-white/5 !border-white/10' : '!bg-white !border-slate-200'
              }`}
            >
              <Skeleton.Input 
                active 
                size="small" 
                style={{ width: 120, height: 12 }}
                className={isDark ? '!bg-white/10' : ''}
              />
              <Skeleton.Input 
                active 
                size="large" 
                style={{ width: 80, height: 32, marginTop: 12 }}
                className={isDark ? '!bg-white/10' : ''}
              />
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Monthly Roadmap Skeleton */}
      <Card
        className={`rounded-3xl ${
          isDark ? '!bg-white/5 !border-white/10' : '!bg-white !border-slate-200'
        }`}
      >
        <Skeleton.Input 
          active 
          size="small" 
          style={{ width: 200, height: 16, marginBottom: 20 }}
          className={isDark ? '!bg-white/10' : ''}
        />
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
            >
              <Card
                className={`min-w-[120px] ${
                  isDark ? '!bg-white/5 !border-white/10' : '!bg-slate-50 !border-slate-100'
                }`}
              >
                <Skeleton.Input 
                  active 
                  size="small" 
                  style={{ width: 80, height: 12 }}
                  className={isDark ? '!bg-white/10' : ''}
                />
                <Skeleton.Input 
                  active 
                  size="small" 
                  style={{ width: 60, height: 20, marginTop: 8 }}
                  className={isDark ? '!bg-white/10' : ''}
                />
              </Card>
            </motion.div>
          ))}
        </div>
      </Card>
    </motion.div>
  );
};

// Skeleton for Team Members List
export const TeamMembersSkeleton = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      {[1, 2, 3, 4, 5, 6].map((index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1, duration: 0.3 }}
        >
          <Card
            className={`rounded-2xl ${
              isDark ? '!bg-white/5 !border-white/10' : '!bg-white !border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <Skeleton.Avatar 
                active 
                size={48} 
                className={isDark ? '!bg-white/10' : ''}
              />
              <div className="flex-1">
                <Skeleton.Input 
                  active 
                  size="small" 
                  style={{ width: 120, height: 16 }}
                  className={isDark ? '!bg-white/10' : ''}
                />
                <Skeleton.Input 
                  active 
                  size="small" 
                  style={{ width: 100, height: 12, marginTop: 8 }}
                  className={isDark ? '!bg-white/10' : ''}
                />
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </motion.div>
  );
};
